import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** Modes de gestion possibles (`settings.accountType`). */
export type TutorialAccountType = 'joint_only' | 'joint_with_personal' | 'personal_with_transfers';

export type TutorialStep = {
  id: string;
  /**
   * Route où cette étape doit s'afficher, query comprise (`/dashboard/reglages?tab=shared`).
   * Absente = étape « sur place » : le tutoriel ne navigue pas (cas des étapes qui
   * pointent le menu, présent sur toutes les pages).
   */
  page?: string;
  targetSelector: string; // Sélecteur CSS de l'élément à mettre en évidence
  titleKey: string; // Clé de traduction pour le titre
  contentKey: string; // Clé de traduction pour le contenu
  position: 'top' | 'bottom' | 'left' | 'right';
  requiresInteraction?: boolean; // Si l'utilisateur doit cliquer sur l'élément
  hintKey?: string; // Clé de traduction pour le hint d'interaction
  /** Cible qui peut légitimement ne pas exister : l'étape est alors sautée. */
  optional?: boolean;
  /** Modes de gestion pour lesquels l'étape n'a pas de sens. */
  hideForAccountTypes?: TutorialAccountType[];
};

interface TutorialState {
  isActive: boolean;
  currentStepIndex: number;
  hasCompletedOnce: boolean;
  isNewUser: boolean; // Pour déclencher automatiquement

  // Actions
  startTutorial: () => void;
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (index: number) => void;
  exitTutorial: () => void;
  completeTutorial: () => void;
  setNewUser: (isNew: boolean) => void;
  resetTutorial: () => void;
}

export const useTutorialStore = create<TutorialState>()(
  persist(
    (set) => ({
      isActive: false,
      currentStepIndex: 0,
      hasCompletedOnce: false,
      isNewUser: false,

      startTutorial: () =>
        set({
          isActive: true,
          currentStepIndex: 0,
        }),

      nextStep: () =>
        set((state) => ({
          currentStepIndex: state.currentStepIndex + 1,
        })),

      previousStep: () =>
        set((state) => ({
          currentStepIndex: Math.max(0, state.currentStepIndex - 1),
        })),

      goToStep: (index) =>
        set({
          currentStepIndex: index,
        }),

      exitTutorial: () =>
        set({
          isActive: false,
        }),

      completeTutorial: () =>
        set({
          isActive: false,
          hasCompletedOnce: true,
          isNewUser: false,
        }),

      setNewUser: (isNew) =>
        set({
          isNewUser: isNew,
        }),

      resetTutorial: () =>
        set({
          currentStepIndex: 0,
          hasCompletedOnce: false,
        }),
    }),
    {
      name: 'splinity-tutorial',
      partialize: (state) => ({
        hasCompletedOnce: state.hasCompletedOnce,
        isNewUser: state.isNewUser,
      }),
    }
  )
);

/**
 * Étapes du tutoriel (clés de traduction dans `tutorial.json`).
 *
 * Parti pris : une visite guidée des **rubriques**, pas un mode d'emploi champ par
 * champ. Une étape par page — ce qu'on y trouve, en une phrase ou deux — et les
 * détails (récurrence, transferts internes, revenus partagés…) laissés à l'usage
 * et à l'aide contextuelle. Les étapes qui pointent le menu n'ont pas de `page` :
 * elles s'affichent là où l'utilisateur se trouve.
 */
export const tutorialSteps: TutorialStep[] = [
  // ─── Tableau de bord ───
  { id: 'welcome', page: '/dashboard', targetSelector: '[data-tutorial="dashboard-title"]', titleKey: 'welcome.title', contentKey: 'welcome.content', position: 'bottom' },
  { id: 'navigation', targetSelector: '[data-tutorial="main-nav"]', titleKey: 'navigation.title', contentKey: 'navigation.content', position: 'right' },
  { id: 'dashboard-period', page: '/dashboard', targetSelector: '[data-tutorial="dashboard-period"]', titleKey: 'dashboardPeriod.title', contentKey: 'dashboardPeriod.content', position: 'bottom' },
  // Les onglets n'existent que si les deux budgets sont visibles à la fois
  { id: 'dashboard-tabs', page: '/dashboard', targetSelector: '[data-tutorial="dashboard-tabs"]', titleKey: 'dashboardTabs.title', contentKey: 'dashboardTabs.content', position: 'bottom', optional: true, hideForAccountTypes: ['joint_only', 'personal_with_transfers'] },
  { id: 'dashboard-customize', page: '/dashboard', targetSelector: '[data-tutorial="dashboard-customize"]', titleKey: 'dashboardCustomize.title', contentKey: 'dashboardCustomize.content', position: 'bottom' },

  // ─── Compte commun ───
  { id: 'nav-compte-commun', targetSelector: '[data-tutorial="nav-compte-commun"]', titleKey: 'navCompteCommun.title', contentKey: 'navCompteCommun.content', position: 'right', requiresInteraction: true, hintKey: 'navCompteCommun.hint', hideForAccountTypes: ['personal_with_transfers'] },
  { id: 'shared-operations', page: '/dashboard/compte-commun', targetSelector: '[data-tutorial="operations-header"]', titleKey: 'sharedOperations.title', contentKey: 'sharedOperations.content', position: 'bottom', hideForAccountTypes: ['personal_with_transfers'] },

  // ─── Mon budget ───
  { id: 'nav-mon-budget', targetSelector: '[data-tutorial="nav-mon-budget"]', titleKey: 'navMonBudget.title', contentKey: 'navMonBudget.content', position: 'right', requiresInteraction: true, hintKey: 'navMonBudget.hint', hideForAccountTypes: ['joint_only'] },
  { id: 'personal-operations', page: '/dashboard/mon-budget', targetSelector: '[data-tutorial="operations-header"]', titleKey: 'personalOperations.title', contentKey: 'personalOperations.content', position: 'bottom', hideForAccountTypes: ['joint_only'] },

  // ─── Réglages ───
  { id: 'nav-reglages', targetSelector: '[data-tutorial="nav-reglages"]', titleKey: 'navReglages.title', contentKey: 'navReglages.content', position: 'right', requiresInteraction: true, hintKey: 'navReglages.hint' },
  { id: 'settings-overview', page: '/dashboard/reglages?tab=shared', targetSelector: '[data-tutorial="reglages-title"]', titleKey: 'settingsOverview.title', contentKey: 'settingsOverview.content', position: 'bottom' },

  // ─── Profil ───
  { id: 'nav-profil', targetSelector: '[data-tutorial="nav-profil"]', titleKey: 'navProfil.title', contentKey: 'navProfil.content', position: 'right', requiresInteraction: true, hintKey: 'navProfil.hint' },
  { id: 'profile-overview', page: '/dashboard/profil?tab=personal', targetSelector: '[data-tutorial="profil-title"]', titleKey: 'profileOverview.title', contentKey: 'profileOverview.content', position: 'bottom' },
  { id: 'tutorial-end', page: '/dashboard/profil?tab=personal', targetSelector: '[data-tutorial="restart-tutorial"]', titleKey: 'tutorialEnd.title', contentKey: 'tutorialEnd.content', position: 'top' },
];

/**
 * Étapes applicables au mode de gestion courant : en « compte commun seul » il n'y
 * a pas de budget personnel à visiter, et inversement. Le filtrage doit être fait
 * partout où l'on interroge l'étape courante, sinon les index divergent.
 */
export function getTutorialSteps(accountType?: string | null): TutorialStep[] {
  if (!accountType) return tutorialSteps;
  return tutorialSteps.filter(
    (step) => !step.hideForAccountTypes?.includes(accountType as TutorialAccountType)
  );
}
